import React, { useState, useRef, useCallback, useEffect } from "react";
import { Video, Mic, Type, X, Check, Square, RotateCcw, Camera, Loader2, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";

export type IntroType = "video" | "voice" | "text";

interface IntroCapsuleRecorderProps {
  onIntroReady: (type: IntroType, content: Blob | string, duration?: number) => void;
  onClear: () => void;
  currentIntro: { type: IntroType; content: Blob | string; duration?: number } | null;
  mode?: "serious" | "casual" | "deep" | "anonymous"; // Portal mode for AI prompts
}

const MAX_VIDEO_DURATION = 90;
const MAX_VOICE_DURATION = 60;
const MAX_TEXT_LENGTH = 280;

// AI prompt suggestions based on mode
const getAIPrompts = (mode?: "serious" | "casual" | "deep" | "anonymous"): string[] => {
  const basePrompts = [
    "What made you want to open this Portal?",
    "Share a moment that defines connection for you.",
    "What's one thing you're curious to explore together?",
  ];

  const modePrompts: Record<string, string[]> = {
    serious: [
      "What are you looking for in a meaningful relationship?",
      "Share what 'serious' means to you.",
      "What values matter most to you in a partner?",
    ],
    casual: [
      "What's your idea of a perfect casual connection?",
      "Share something fun you'd love to do together.",
      "What makes you smile?",
    ],
    deep: [
      "What's a quiet moment that defines deep talk for you?",
      "Share a thought you've been holding close.",
      "What conversation are you most curious to have?",
    ],
    anonymous: [
      "What made you want to connect anonymously first?",
      "Share something you'd say if no one knew it was you.",
      "What are you hoping to discover?",
    ],
  };

  return mode ? modePrompts[mode] || basePrompts : basePrompts;
};

const IntroCapsuleRecorder = ({ onIntroReady, onClear, currentIntro, mode }: IntroCapsuleRecorderProps) => {
  const [selectedType, setSelectedType] = useState<IntroType | null>(currentIntro?.type || null);
  const [isRecording, setIsRecording] = useState(false);
  const [textContent, setTextContent] = useState(
    currentIntro?.type === "text" ? (currentIntro.content as string) : ""
  );
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const aiPrompts = getAIPrompts(mode);
  
  // Video recording state
  const [videoBlob, setVideoBlob] = useState<Blob | null>(
    currentIntro?.type === "video" ? (currentIntro.content as Blob) : null
  );
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState(currentIntro?.duration || 0);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Voice recording
  const {
    isRecording: isVoiceRecording,
    recordingDuration: voiceDuration,
    audioBlob,
    startRecording: startVoiceRecording,
    stopRecording: stopVoiceRecording,
    cancelRecording: cancelVoiceRecording,
    resetRecording: resetVoiceRecording,
  } = useAudioRecorder();

  // Set intro when audio is ready
  useEffect(() => {
    if (audioBlob && selectedType === "voice") {
      onIntroReady("voice", audioBlob, voiceDuration);
    }
  }, [audioBlob, voiceDuration, selectedType, onIntroReady]);

  // Initialize camera for video
  const initCamera = useCallback(async () => {
    try {
      setCameraReady(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraReady(true);
      }
    } catch (err) {
      console.error("Camera error:", err);
    }
  }, [facingMode]);

  // Start video recording
  const startVideoRecording = useCallback(() => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    setVideoDuration(0);
    setVideoBlob(null);
    setVideoUrl(null);

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : "video/webm";

    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType,
      videoBitsPerSecond: 2000000,
    });

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setVideoBlob(blob);
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(100);
    setIsRecording(true);

    let elapsed = 0;
    timerRef.current = setInterval(() => {
      elapsed += 0.1;
      setVideoDuration(elapsed);
      if (elapsed >= MAX_VIDEO_DURATION) {
        stopVideoRecording();
      }
    }, 100);
  }, []);

  const stopVideoRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  // Cleanup on type change or unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  useEffect(() => {
    if (selectedType === "video" && !videoBlob) {
      initCamera();
    }
  }, [selectedType, videoBlob, initCamera]);

  // Confirm video
  const confirmVideo = useCallback(() => {
    if (videoBlob && videoDuration >= 3) {
      onIntroReady("video", videoBlob, videoDuration);
    }
  }, [videoBlob, videoDuration, onIntroReady]);

  const reRecordVideo = useCallback(() => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoBlob(null);
    setVideoUrl(null);
    setVideoDuration(0);
    initCamera();
  }, [videoUrl, initCamera]);

  // Handle text save
  const saveText = () => {
    if (textContent.trim().length > 0 && textContent.length <= MAX_TEXT_LENGTH) {
      onIntroReady("text", textContent.trim());
    }
  };

  // Clear intro
  const handleClear = () => {
    setSelectedType(null);
    setTextContent("");
    setVideoBlob(null);
    setVideoUrl(null);
    setVideoDuration(0);
    resetVoiceRecording();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    onClear();
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Type selector
  if (!selectedType) {
    return (
      <div className="space-y-4">
        <div className="p-3 rounded-xl bg-muted/50 border border-dashed border-border">
          <p className="text-sm font-medium text-center mb-1">Add an intro capsule (optional)</p>
          <p className="text-xs text-muted-foreground text-center">
            This helps them understand who you are before accepting.
          </p>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => setSelectedType("video")}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/50 transition-all"
          >
            <div className="p-2 rounded-lg bg-rose-500/10">
              <Video className="w-5 h-5 text-rose-500" />
            </div>
            <span className="text-xs font-medium">Video</span>
            <span className="text-[10px] text-muted-foreground">≤90s</span>
          </button>
          
          <button
            onClick={() => setSelectedType("voice")}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/50 transition-all"
          >
            <div className="p-2 rounded-lg bg-violet-500/10">
              <Mic className="w-5 h-5 text-violet-500" />
            </div>
            <span className="text-xs font-medium">Voice</span>
            <span className="text-[10px] text-muted-foreground">≤60s</span>
          </button>
          
          <button
            onClick={() => setSelectedType("text")}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/50 transition-all"
          >
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Type className="w-5 h-5 text-blue-500" />
            </div>
            <span className="text-xs font-medium">Text</span>
            <span className="text-[10px] text-muted-foreground">≤280</span>
          </button>
        </div>
        
        {/* AI Prompt Suggestions */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground text-center">
            💡 AI Prompt Suggestions
          </p>
          <div className="space-y-1.5">
            {aiPrompts.slice(0, 3).map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setSelectedPrompt(prompt);
                  setSelectedType("text");
                  setTextContent(prompt);
                }}
                className="w-full p-2.5 rounded-lg bg-primary/5 border border-primary/20 hover:border-primary/40 hover:bg-primary/10 transition-all text-left"
              >
                <p className="text-xs text-foreground italic">"{prompt}"</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Video recorder UI
  if (selectedType === "video") {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Video Intro</span>
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <X className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
        
        <div className="relative aspect-[9/16] max-h-64 bg-black rounded-xl overflow-hidden">
          {videoUrl ? (
            <video
              src={videoUrl}
              className="w-full h-full object-cover"
              autoPlay
              loop
              playsInline
              muted
            />
          ) : (
            <video
              ref={videoRef}
              className={cn(
                "w-full h-full object-cover",
                facingMode === "user" && "scale-x-[-1]"
              )}
              autoPlay
              playsInline
              muted
            />
          )}
          
          {isRecording && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-destructive/90">
              <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span className="text-white text-sm font-medium">{formatDuration(videoDuration)}</span>
            </div>
          )}
        </div>
        
        <div className="flex justify-center gap-4">
          {videoUrl ? (
            <>
              <Button variant="outline" onClick={reRecordVideo}>
                <RotateCcw className="w-4 h-4 mr-2" /> Retake
              </Button>
              <Button onClick={confirmVideo} disabled={videoDuration < 3}>
                <Check className="w-4 h-4 mr-2" /> Use Video
              </Button>
            </>
          ) : isRecording ? (
            <Button onClick={stopVideoRecording} variant="destructive">
              <Square className="w-4 h-4 mr-2 fill-white" /> Stop ({formatDuration(videoDuration)})
            </Button>
          ) : (
            <Button onClick={startVideoRecording} disabled={!cameraReady}>
              <Camera className="w-4 h-4 mr-2" /> {cameraReady ? "Start Recording" : "Loading..."}
            </Button>
          )}
        </div>
        
        <p className="text-xs text-muted-foreground text-center">
          Keep it under 90 seconds. Be yourself!
        </p>
      </div>
    );
  }

  // Voice recorder UI
  if (selectedType === "voice") {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Voice Intro</span>
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <X className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
        
        <div className="p-6 rounded-xl bg-muted/50 border border-border">
          {audioBlob ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-sm font-medium">Voice ready ({formatDuration(voiceDuration)})</p>
              <Button variant="outline" size="sm" onClick={() => { resetVoiceRecording(); onClear(); }}>
                <RotateCcw className="w-4 h-4 mr-2" /> Re-record
              </Button>
            </div>
          ) : isVoiceRecording ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center animate-pulse">
                <Mic className="w-8 h-8 text-destructive" />
              </div>
              <p className="text-sm font-medium">Recording... {formatDuration(voiceDuration)}</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={cancelVoiceRecording}>
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={stopVoiceRecording} disabled={voiceDuration < 3}>
                  <Square className="w-4 h-4 mr-1 fill-current" /> Stop
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-violet-500/10 flex items-center justify-center">
                <Mic className="w-8 h-8 text-violet-500" />
              </div>
              <Button onClick={startVoiceRecording}>
                <Mic className="w-4 h-4 mr-2" /> Start Recording
              </Button>
            </div>
          )}
        </div>
        
        <p className="text-xs text-muted-foreground text-center">
          Keep it under 60 seconds.
        </p>
      </div>
    );
  }

  // Text intro UI
  if (selectedType === "text") {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Text Intro</span>
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <X className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
        
        <div>
          {/* AI Prompt Suggestions for text */}
          {!textContent && (
            <div className="mb-3 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">💡 Try these prompts:</p>
              {aiPrompts.slice(0, 2).map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => setTextContent(prompt)}
                  className="w-full p-2 rounded-lg bg-primary/5 border border-primary/20 hover:border-primary/40 text-xs text-left transition-all"
                >
                  "{prompt}"
                </button>
              ))}
            </div>
          )}
          
          <Textarea
            placeholder={selectedPrompt || "What made you want to open a Portal with this person?"}
            value={textContent}
            onChange={(e) => {
              setTextContent(e.target.value);
              setSelectedPrompt(null);
            }}
            rows={4}
            maxLength={MAX_TEXT_LENGTH}
            className="resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              {textContent.length}/{MAX_TEXT_LENGTH}
            </p>
            <Button
              size="sm"
              onClick={saveText}
              disabled={textContent.trim().length === 0}
            >
              <Check className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default IntroCapsuleRecorder;
